import dayjs from 'dayjs';

import {
  IAnalyticsStackedTimeSeriesPoint,
  IAnalyticsStackedTimeSeriesSegment
} from 'components/AnalyticsBox/AnalyticsStackedTimeSeriesBox/AnalyticsStackedTimeSeriesBox.types';

import {
  ApiPerformanceSegmentKey,
  IApiPerformanceApiMetric,
  IApiPerformanceIndicator,
  IApiPerformanceTimeSeriesPoint,
  SlaApiName
} from './AnalyticsApiPerformanceTab.types';

const MONTH_START_FORMAT = 'YYYY-MM-01';
const X_AXIS_DATE_FORMAT = 'DD-MM-YYYY';
const TOOLTIP_DATE_FORMAT = 'MMM D, YYYY';

export const X_AXIS_TICK_ROTATION_DEGREES = -45;

const APPCHARGE_PROCESSING_LABEL = 'Appcharge processing';
const PUBLISHER_PROCESSING_LABEL = 'Publisher processing';

const APPCHARGE_PROCESSING_COLOR_TOKEN = 'var(--base-indigo-200)';
const PUBLISHER_PROCESSING_COLOR_TOKEN = 'var(--base-indigo-600)';
const PUBLISHER_PROCESSING_GRADIENT_TOP_COLOR_TOKEN = 'var(--base-indigo-300)';
const PUBLISHER_PROCESSING_GRADIENT_BOTTOM_COLOR_TOKEN = 'var(--base-indigo-600)';

export const PRIMARY_METRIC_LABEL = 'API total response time';
export const PRIMARY_METRIC_STATISTIC_LABEL = 'p95';

const API_METRIC_DISPLAY_NAME_BY_API_NAME: Partial<Record<SlaApiName, string>> = {
  [SlaApiName.AuthLogin]: 'Authenticate Player Callback',
  [SlaApiName.AuthOtp]: 'Initiate Game Auth Callback',
  [SlaApiName.Award]: 'Grant Award Callback',
  [SlaApiName.Personalization]: 'Personalize Web Store Callback',
  [SlaApiName.PublisherEvents]: 'Events Webhook'
};

export const resolveApiMetricDisplayName = (
  apiMetric: IApiPerformanceApiMetric
): string => {
  return (
    API_METRIC_DISPLAY_NAME_BY_API_NAME[apiMetric.apiName] ??
    apiMetric.displayName
  );
};

export const API_PERFORMANCE_API_NAMES: readonly SlaApiName[] = [
  SlaApiName.AuthLogin,
  SlaApiName.AuthOtp,
  SlaApiName.Award,
  SlaApiName.Personalization,
  SlaApiName.PublisherEvents
];

const buildEmptyIndicator = (): IApiPerformanceIndicator => ({
  p95TotalDurationMs: null,
  p95AppchargeProcessingDurationMs: null,
  p95PublisherProcessingDurationMs: null
});

const buildPlaceholderApiMetric = (
  apiName: SlaApiName
): IApiPerformanceApiMetric => ({
  apiName,
  displayName: API_METRIC_DISPLAY_NAME_BY_API_NAME[apiName] ?? apiName,
  indicator: buildEmptyIndicator(),
  timeSeries: []
});

export const buildPlaceholderApiMetrics = (): IApiPerformanceApiMetric[] => {
  return API_PERFORMANCE_API_NAMES.map(buildPlaceholderApiMetric);
};

export const formatMonthForBackend = (selectedMonth: Date): string => {
  return dayjs(selectedMonth).startOf('month').format(MONTH_START_FORMAT);
};

export const formatXAxisTick = (dateString: string): string => {
  return dayjs(dateString).format(X_AXIS_DATE_FORMAT);
};

export const formatTooltipTitle = (dateString: string): string => {
  return dayjs(dateString).format(TOOLTIP_DATE_FORMAT);
};

const coerceNullToUndefined = (value: number | null): number | undefined => {
  return value ?? undefined;
};

export const mapTimeSeriesPointToStackedPoint = (
  point: IApiPerformanceTimeSeriesPoint
): IAnalyticsStackedTimeSeriesPoint => {
  return {
    date: point.date,
    totalValue: coerceNullToUndefined(point.p95TotalDurationMs),
    segmentValues: {
      [ApiPerformanceSegmentKey.AppchargeProcessing]: coerceNullToUndefined(
        point.p95AppchargeProcessingDurationMs
      ),
      [ApiPerformanceSegmentKey.PublisherProcessing]: coerceNullToUndefined(
        point.p95PublisherProcessingDurationMs
      )
    }
  };
};

export const buildApiPerformanceSegments = (
  apiMetric: IApiPerformanceApiMetric
): IAnalyticsStackedTimeSeriesSegment[] => {
  return [
    {
      key: ApiPerformanceSegmentKey.PublisherProcessing,
      label: PUBLISHER_PROCESSING_LABEL,
      color: PUBLISHER_PROCESSING_COLOR_TOKEN,
      value: coerceNullToUndefined(
        apiMetric.indicator.p95PublisherProcessingDurationMs
      ),
      gradient: {
        topColor: PUBLISHER_PROCESSING_GRADIENT_TOP_COLOR_TOKEN,
        bottomColor: PUBLISHER_PROCESSING_GRADIENT_BOTTOM_COLOR_TOKEN
      }
    },
    {
      key: ApiPerformanceSegmentKey.AppchargeProcessing,
      label: APPCHARGE_PROCESSING_LABEL,
      color: APPCHARGE_PROCESSING_COLOR_TOKEN,
      value: coerceNullToUndefined(
        apiMetric.indicator.p95AppchargeProcessingDurationMs
      )
    }
  ];
};

export const resolveIndicatorPrimaryValue = (
  apiMetric: IApiPerformanceApiMetric
): number | undefined => {
  return coerceNullToUndefined(apiMetric.indicator.p95TotalDurationMs);
};
